// Copyright (c) StrangeBee 2023

import React, { ReactElement } from 'react';
import CaseListTitle from '@Organisms/Case/CaseListTitle/CaseListTitle';
import { THList } from '@Molecules/THList';
import { columnsRenderer } from '@Hooks/useConfigList/helper/computeHeader';
import { useLocalMessage } from '@Helpers/locale';
import { ColumnTitle } from '@Atoms/ColumnTitle/ColumnTitle';
import { FormatDate } from '@Molecules/Format';
import { ObservableOutput, AlertOutput, CaseOutput } from 'thehive-sdk';
import { useHistory } from 'react-router-dom';
import RouteBuilder from '@Services/RouteBuilder';
import { AlertListTitle } from '@Organisms/Alert/AlertListTitle/AlertListTitle';
import { PatternType } from '@Constants/patternsRoute';
import { ObservableListStatus } from '../ObservableListStatus/ObservableListStatus';

import './ObservableLinkList.scss';

interface ObservableLinkListProps {
    similarData: ObservableOutput[];
}

const ObservableLinkList = ({ similarData }: ObservableLinkListProps): ReactElement => {
    const i = useLocalMessage();
    const history = useHistory();

    const columns = columnsRenderer<ObservableOutput>([
        {
            title: <ColumnTitle>{i('common.flags')}</ColumnTitle>,
            className: 'observable-links__flags',
            render: ({ extraData, ...observableData }): ReactElement => {
                const {
                    tlp,
                    pap,
                    ioc,
                    sighted,
                    ignoreSimilarity = false,
                } = extraData?.links?.observable || observableData;

                const emptyFunction = (): void => {
                    return;
                };
                return (
                    <ObservableListStatus
                        values={{ tlp, ioc, pap, sighted, ignoreSimilarity }}
                        onStatusClick={{
                            onTLPClick: emptyFunction,
                            onPAPClick: emptyFunction,
                            onIOCClick: emptyFunction,
                            onSightedClick: emptyFunction,
                            onSimilarityClick: emptyFunction,
                        }}
                    />
                );
            },
        },
        {
            title: <ColumnTitle>{i('alert.table.columns.source')}</ColumnTitle>,
            className: 'observable-links__source',
            render: ({ extraData }) => {
                const linkdata = extraData?.links?.observable || extraData?.links;
                const suffixLinks = extraData?.links?.observable ? '/' + i('object-types.observable') : '';

                const typeLinks = linkdata?.case
                    ? i('object-types.case')
                    : linkdata?.alert
                    ? i('object-types.alert')
                    : '';
                return <p>{`${typeLinks}${suffixLinks}`}</p>;
            },
        },
        {
            title: <ColumnTitle>{i('general.details')}</ColumnTitle>,
            render: ({ extraData, _id: observableId }) => {
                let paramsUrl = [] as unknown[];
                let parentData = {} as unknown;

                const parentId = extraData?.links?.observable?._id || observableId;
                const linkdata = extraData?.links?.observable || extraData?.links;
                const typeLinks = linkdata?.case ? 'case' : linkdata?.alert ? 'alert' : 'observable';

                switch (typeLinks) {
                    case 'alert':
                        parentData = linkdata?.alert;
                        paramsUrl = [
                            'alerts.observables' as PatternType,
                            { alertId: (parentData as AlertOutput)?._id },
                        ];
                        return (
                            <AlertListTitle
                                path={RouteBuilder.compute.apply(
                                    RouteBuilder,
                                    paramsUrl as [PatternType, Record<string, string>],
                                )}
                                data={parentData as AlertOutput}
                                tagsIsOpen={false}
                                customFieldsIsOpen={false}
                            />
                        );
                    case 'case':
                        parentData = linkdata?.case;
                        paramsUrl = [
                            'cases.observable' as PatternType,
                            { caseId: (parentData as CaseOutput)?._id, observableId: parentId },
                        ];
                        return (
                            <CaseListTitle
                                onLinkClick={() =>
                                    history.push(
                                        RouteBuilder.compute.apply(
                                            RouteBuilder,
                                            paramsUrl as [PatternType, Record<string, string>],
                                        ),
                                    )
                                }
                                data={parentData as CaseOutput}
                                tagsIsOpen={false}
                                customFieldsIsOpen={false}
                            />
                        );
                }

                return null;
            },
        },
        {
            title: <ColumnTitle>{i('common.created-at')}</ColumnTitle>,
            className: 'observable-link-list__created-at',
            render: ({ _createdAt }) => <FormatDate date={_createdAt} defaultStyle={false} />,
        },
    ]);

    return (
        <THList>
            <THList.Content columns={columns} dataSource={similarData} rowKey="_id" />
        </THList>
    );
};

export default ObservableLinkList;
